import React, { useState, useEffect, useCallback } from "react";
import {
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  TagIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const ApprovalMatrixConfig = ({
  approvalMatrix = [],
  onChange,
  viewOnly = false,
}) => {
  const APPROVAL_TYPES = {
    HIGH_VALUE: "High Value Transaction",
    RATE_CHANGE: "Rate Change",
    DISCOUNT: "Discount Approval",
    REDEMPTION: "Redemption",
  };

  const APPROVAL_ROLES = [
    "Manager",
    "Senior Manager",
    "Branch Head",
    "Regional Manager",
    "Director",
    "Owner",
  ];

  const [matrix, setMatrix] = useState(approvalMatrix);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newApproval, setNewApproval] = useState({
    type: "HIGH_VALUE",
    threshold: "",
    approverRole: "Manager",
    approverIds: [],
    conditions: {
      timeRestricted: false,
      startTime: "",
      endTime: "",
      daysOfWeek: [],
      requiresMultipleApprovers: false,
      approverCount: 1,
    },
  });

  // Fix: Only sync with parent when matrix actually changes
  // Remove onChange from dependencies to prevent infinite loop
  useEffect(() => {
    if (!viewOnly && onChange) {
      // Only call onChange if matrix is different from approvalMatrix prop
      const matrixChanged =
        JSON.stringify(matrix) !== JSON.stringify(approvalMatrix);
      if (matrixChanged) {
        onChange(matrix);
      }
    }
  }, [matrix, viewOnly]); // Removed onChange from dependencies

  // Reset internal state when prop changes from outside
  useEffect(() => {
    setMatrix(approvalMatrix);
  }, [approvalMatrix]);

  const getApprovalIcon = (type) => {
    switch (type) {
      case "HIGH_VALUE":
        return <CurrencyRupeeIcon className="h-5 w-5" />;
      case "RATE_CHANGE":
        return <RefreshIcon className="h-5 w-5" />;
      case "DISCOUNT":
        return <TagIcon className="h-5 w-5" />;
      case "REDEMPTION":
        return <UserGroupIcon className="h-5 w-5" />;
      default:
        return <ShieldCheckIcon className="h-5 w-5" />;
    }
  };

  const addApprovalRule = () => {
    if (!newApproval.threshold || !newApproval.approverRole) return;

    const rule = {
      ...newApproval,
      id: Date.now(),
      threshold: parseFloat(newApproval.threshold),
    };

    setMatrix([...matrix, rule]);
    setNewApproval({
      type: "HIGH_VALUE",
      threshold: "",
      approverRole: "Manager",
      approverIds: [],
      conditions: {
        timeRestricted: false,
        startTime: "",
        endTime: "",
        daysOfWeek: [],
        requiresMultipleApprovers: false,
        approverCount: 1,
      },
    });
    setShowAddForm(false);
  };

  const removeApprovalRule = (id) => {
    setMatrix(matrix.filter((rule) => rule.id !== id));
  };

  const updateApprovalRule = (id, field, value) => {
    setMatrix(
      matrix.map((rule) =>
        rule.id === id ? { ...rule, [field]: value } : rule
      )
    );
  };

  const formatThreshold = (type, value) => {
    if (type === "DISCOUNT") {
      return `${value}%`;
    }
    return `₹${value.toLocaleString("en-IN")}`;
  };

  const getApprovalTypeColor = (type) => {
    switch (type) {
      case "HIGH_VALUE":
        return "bg-blue-100 text-blue-700";
      case "RATE_CHANGE":
        return "bg-green-100 text-green-700";
      case "DISCOUNT":
        return "bg-yellow-100 text-yellow-700";
      case "REDEMPTION":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getRoleLevel = (role) => {
    const levels = {
      Manager: 1,
      "Senior Manager": 2,
      "Branch Head": 3,
      "Regional Manager": 4,
      Director: 5,
      Owner: 6,
    };
    return levels[role] || 0;
  };

  // Group and sort rules by type
  const groupedRules = matrix.reduce((acc, rule) => {
    if (!acc[rule.type]) acc[rule.type] = [];
    acc[rule.type].push(rule);
    return acc;
  }, {});

  // Sort rules within each group by threshold
  Object.keys(groupedRules).forEach((type) => {
    groupedRules[type].sort((a, b) => a.threshold - b.threshold);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            Approval Matrix Configuration
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Define approval requirements for different transaction types
          </p>
        </div>
        {!viewOnly && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            Add Rule
          </button>
        )}
      </div>

      {/* Add Rule Form */}
      {showAddForm && !viewOnly && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold mb-3">Add Approval Rule</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Approval Type
              </label>
              <select
                value={newApproval.type}
                onChange={(e) =>
                  setNewApproval({ ...newApproval, type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {Object.entries(APPROVAL_TYPES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Threshold {newApproval.type === "DISCOUNT" ? "(%)" : "(₹)"}
              </label>
              <input
                type="number"
                value={newApproval.threshold}
                onChange={(e) =>
                  setNewApproval({ ...newApproval, threshold: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
                placeholder={newApproval.type === "DISCOUNT" ? "10" : "50000"}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Approver Role
              </label>
              <select
                value={newApproval.approverRole}
                onChange={(e) =>
                  setNewApproval({
                    ...newApproval,
                    approverRole: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {APPROVAL_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newApproval.conditions.requiresMultipleApprovers}
                  onChange={(e) =>
                    setNewApproval({
                      ...newApproval,
                      conditions: {
                        ...newApproval.conditions,
                        requiresMultipleApprovers: e.target.checked,
                      },
                    })
                  }
                  className="h-4 w-4 text-blue-600 rounded mr-2"
                />
                <span className="text-sm">Requires multiple approvers</span>
              </label>
              {newApproval.conditions.requiresMultipleApprovers && (
                <input
                  type="number"
                  value={newApproval.conditions.approverCount}
                  onChange={(e) =>
                    setNewApproval({
                      ...newApproval,
                      conditions: {
                        ...newApproval.conditions,
                        approverCount: parseInt(e.target.value) || 1,
                      },
                    })
                  }
                  className="mt-2 w-full px-2 py-1 border border-gray-300 rounded"
                  placeholder="Number of approvers"
                  min="2"
                  max="5"
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewApproval({
                  type: "HIGH_VALUE",
                  threshold: "",
                  approverRole: "Manager",
                  approverIds: [],
                  conditions: {
                    timeRestricted: false,
                    startTime: "",
                    endTime: "",
                    daysOfWeek: [],
                    requiresMultipleApprovers: false,
                    approverCount: 1,
                  },
                });
              }}
              className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addApprovalRule}
              disabled={!newApproval.threshold}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Add Rule
            </button>
          </div>
        </div>
      )}

      {/* Approval Rules Display */}
      {matrix.length > 0 ? (
        <div className="space-y-4">
          {Object.entries(APPROVAL_TYPES).map(([type, typeName]) => {
            const rules = groupedRules[type] || [];
            if (rules.length === 0 && viewOnly) return null;

            return (
              <div key={type} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  {getApprovalIcon(type)}
                  <h4 className="font-semibold">{typeName}</h4>
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full ${getApprovalTypeColor(
                      type
                    )}`}
                  >
                    {rules.length} rules
                  </span>
                </div>

                {rules.length > 0 ? (
                  <div className="space-y-2">
                    {rules.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-sm text-gray-600">Above</span>
                            <p className="font-semibold">
                              {formatThreshold(rule.type, rule.threshold)}
                            </p>
                          </div>
                          <div className="text-gray-400">→</div>
                          <div>
                            <span className="text-sm text-gray-600">
                              Requires
                            </span>
                            <p className="font-semibold">
                              {rule.approverRole}
                              {rule.conditions?.requiresMultipleApprovers &&
                                ` (${rule.conditions.approverCount} approvers)`}
                            </p>
                          </div>
                        </div>

                        {!viewOnly && (
                          <button
                            type="button"
                            onClick={() => removeApprovalRule(rule.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No rules configured
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ShieldCheckIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p className="text-gray-500">No approval rules configured</p>
          {!viewOnly && (
            <p className="text-sm text-gray-400 mt-1">
              Click "Add Rule" to configure approval requirements
            </p>
          )}
        </div>
      )}

      {/* Summary */}
      {matrix.length > 0 && (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h4 className="font-medium mb-2 text-yellow-900">Approval Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-yellow-700">Total Rules:</span>
              <p className="font-semibold text-yellow-900">{matrix.length}</p>
            </div>
            <div>
              <span className="text-yellow-700">Approval Types:</span>
              <p className="font-semibold text-yellow-900">
                {Object.keys(groupedRules).length}
              </p>
            </div>
            <div>
              <span className="text-yellow-700">Highest Authority:</span>
              <p className="font-semibold text-yellow-900">
                {matrix.reduce((highest, rule) => {
                  const level = getRoleLevel(rule.approverRole);
                  return level > getRoleLevel(highest)
                    ? rule.approverRole
                    : highest;
                }, "Manager")}
              </p>
            </div>
            <div>
              <span className="text-yellow-700">Multi-Approver Rules:</span>
              <p className="font-semibold text-yellow-900">
                {
                  matrix.filter((r) => r.conditions?.requiresMultipleApprovers)
                    .length
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalMatrixConfig;
